import { defineApiRoute as route } from '@/services/apiPath';

/**
 * Every backend route the client calls, relative to the API base URL. Call a route for its request
 * path, e.g. `API_ROUTES.recipes.byId({ recipeId })`; tests and mocks match requests on its `pattern`.
 */
export const API_ROUTES = {
  aiChat: {
    root: route('/v1/ai-chat'),
    conversations: route('/v1/ai-chat/conversations'),
    conversation: route('/v1/ai-chat/conversations/:conversationId'),
  },
  analytics: {
    membersReport: route('/v1/analytics/members-report'),
    usageReport: route('/v1/analytics/usage-report'),
  },
  assets: {
    copyReferences: route('/v1/assets/copy-references'),
    loraMetadata: route('/v1/assets/loras/:loraId/metadata'),
    loraPresignUrl: route('/v1/assets/loras/presign-url'),
    resolve: route('/v1/assets/:workflowId/resolve'),
    shared: route('/v1/assets/shared'),
    uploadAsset: route('/v1/assets/upload-asset'),
  },
  auth: {
    figmaConnect: route('/v1/auth/figma/connect'),
    figmaConnections: route('/v1/auth/figma/connections'),
    figmaOidcToken: route('/v1/auth/figma/oidc/token'),
  },
  batches: {
    cancel: route('/v1/batches/recipes/:recipeId/batches/:batchId/cancel'),
    cost: route('/v1/batches/recipes/:recipeId/cost'),
    execute: route('/v1/batches/recipes/:recipeId/execute'),
    status: route('/v1/batches/recipes/:recipeId/batches/:batchId/status'),
  },
  bi: {
    identify: route('/bi/identify'),
    track: route('/bi/track'),
  },
  /**
   * Proxied to Sinatra by Weave Core, which authorizes on the resource id in the path. A tool is a
   * Community `weave_app`, keyed by its WorkflowTool id rather than a workflow id.
   */
  community: {
    categories: route('/v1/community/categories'),
    handleAvailability: route('/v1/community/handle-availability'),
    publisherProfiles: route('/v1/community/publisher-profiles'),
    toolMetadata: route('/v1/community/tools/:weaveAppId/metadata'),
    toolPublish: route('/v1/community/tools/:weaveAppId/publish'),
    toolUnpublish: route('/v1/community/tools/:weaveAppId/unpublish'),
    toolUploads: route('/v1/community/tools/:weaveAppId/uploads'),
    workflowMetadata: route('/v1/community/workflows/:weaveWorkflowId/metadata'),
    workflowPublish: route('/v1/community/workflows/:weaveWorkflowId/publish'),
    workflowUnpublish: route('/v1/community/workflows/:weaveWorkflowId/unpublish'),
    workflowUploads: route('/v1/community/workflows/:weaveWorkflowId/uploads'),
  },
  credits: {
    windowStatus: route('/v1/credits/window-status'),
  },
  customEffects: {
    glsl: route('/v1/custom-effects/glsl'),
  },
  figmaNode: {
    fileName: route('/v1/figma-node/file-name'),
    fileStatus: route('/v1/figma-node/file-status'),
  },
  figmaPublishing: {
    eligibleOrgs: route('/v1/figma-publishing/eligible-orgs'),
    toolListing: route('/v1/figma-publishing/tools/:weaveAppId/listing'),
    toolOrgPublish: route('/v1/figma-publishing/tools/:weaveAppId/org/publish'),
  },
  folders: {
    root: route('/v1/folders'),
    byId: route('/v1/folders/:folderId'),
    deleted: route('/v1/folders/deleted'),
    list: route('/v1/folders/list'),
    restore: route('/v1/folders/restore'),
  },
  modelRuns: {
    root: route('/v1/model-runs'),
    cost: route('/v1/model-runs/cost'),
    results: route('/v1/model-runs/results'),
    status: route('/v1/model-runs/status'),
  },
  models: {
    civit: route('/v1/models/civit/:civitId'),
    civitVersion: route('/v1/models/civit/version/:versionId'),
    falModel: route('/v1/models/fal/model'),
    imageRegister: route('/v1/models/image/register'),
    cancelPrediction: route('/v1/models/predict/cancel'),
    predictionStatus: route('/v1/models/predict/:predictionId/status'),
    prices: route('/v1/models/prices'),
    replicateModel: route('/v1/models/replicate/:owner/:name'),
    replicateModelVersion: route('/v1/models/replicate/:owner/:name/:version'),
    /** The server only defines owner-qualified Replicate routes; a model id without an owner still calls these. */
    replicateUnownedModel: route('/v1/models/replicate/:modelId'),
    replicateUnownedModelVersion: route('/v1/models/replicate/:modelId/:version'),
    run: route('/v1/models/run'),
    seedance25DraftAvailability: route('/v1/models/seedance-25/draft-availability'),
    transcribe: route('/v1/models/transcribe'),
    transcribeAvailability: route('/v1/models/transcribe/availability'),
  },
  nodeDefinitions: {
    menu: route('/v1/node-definitions/menu'),
    public: route('/v1/node-definitions/public'),
    user: route('/v1/node-definitions/user'),
  },
  recipeRuns: {
    cancel: route('/v1/recipe-runs/recipes/:recipeId/runs/cancel'),
    cost: route('/v1/recipe-runs/recipes/:recipeId/cost'),
    designAppResults: route('/v1/recipe-runs/recipes/:recipeId/user-design-app/results'),
    run: route('/v1/recipe-runs/recipes/:recipeId/run'),
    status: route('/v1/recipe-runs/recipes/:recipeId/runs/status'),
  },
  recipes: {
    root: route('/v1/recipes'),
    byId: route('/v1/recipes/:recipeId'),
    create: route('/v1/recipes/create'),
    public: route('/v1/recipes/public'),
    publicTemplates: route('/v1/recipes/publicTemplates'),
    shared: route('/v1/recipes/shared'),
    auditTrail: route('/v1/recipes/:recipeId/audit-trail'),
    designAppParamsSave: route('/v1/recipes/:recipeId/design-app-params/save'),
    duplicate: route('/v1/recipes/:recipeId/duplicate'),
    fonts: route('/v1/recipes/:recipeId/fonts'),
    move: route('/v1/recipes/:recipeId/move'),
    nodeAuditTrail: route('/v1/recipes/:recipeId/audit-trail/nodes/:nodeId'),
    nodeHistory: route('/v1/recipes/:recipeId/node-history'),
    nodeHistoryByNode: route('/v1/recipes/:recipeId/node-history/:nodeId'),
    nodeHistoryClear: route('/v1/recipes/:recipeId/node-history/:nodeId/clear'),
    nodeHistoryNodeIds: route('/v1/recipes/:recipeId/node-history/node-ids'),
    nodeHistoryVersion: route('/v1/recipes/:recipeId/node-history/:nodeId/versions/:versionId'),
    poster: route('/v1/recipes/:recipeId/poster'),
    prepareCommunityPublish: route('/v1/recipes/:recipeId/prepare-community-publish'),
    publish: route('/v1/recipes/:recipeId/publish'),
    save: route('/v1/recipes/:recipeId/save'),
    sections: route('/v1/recipes/:recipeId/sections'),
    share: route('/v1/recipes/:recipeId/share'),
    sharedDelete: route('/v1/recipes/:recipeId/shared/delete'),
    snapshot: route('/v1/recipes/:recipeId/snapshot'),
    snapshots: route('/v1/recipes/:recipeId/snapshots'),
    snapshotById: route('/v1/recipes/:recipeId/snapshots/:snapshotId'),
    snapshotFork: route('/v1/recipes/:recipeId/snapshots/:snapshotId/fork'),
    snapshotRestore: route('/v1/recipes/:recipeId/snapshots/:snapshotId/restore'),
    toolAudience: route('/v1/recipes/:recipeId/tool-audience'),
    toolConfig: route('/v1/recipes/:recipeId/tool-config'),
    toolConfigLockSnapshot: route('/v1/recipes/:recipeId/tool-config/lock-snapshot'),
    toolConfigLockSnapshotRelease: route('/v1/recipes/:recipeId/tool-config/lock-snapshot/release'),
    userDesignApp: route('/v1/recipes/:recipeId/user-design-app'),
    users: route('/v1/recipes/:recipeId/users'),
    user: route('/v1/recipes/:recipeId/users/:email'),
  },
  security: {
    session: route('/v1/security/session'),
    tier: route('/v1/security/tier'),
  },
  subflowTemplates: {
    root: route('/v1/subflow-templates'),
    byId: route('/v1/subflow-templates/:subflowTemplateId'),
  },
  tools: {
    byId: route('/v1/tools/:toolId'),
    byWorkflow: route('/v1/tools/by-workflow/:workflowId'),
    cost: route('/v1/tools/:toolId/cost'),
    results: route('/v1/tools/:toolId/results'),
    run: route('/v1/tools/:toolId/run'),
    runData: route('/v1/tools/:toolId/run-data'),
    runsCancel: route('/v1/tools/:toolId/runs/cancel'),
    runsStatus: route('/v1/tools/:toolId/runs/status'),
    share: route('/v1/tools/:toolId/share'),
    sharedUsers: route('/v1/tools/:toolId/shared-users'),
    user: route('/v1/tools/:toolId/users/:email'),
    userData: route('/v1/tools/:toolId/user-data'),
    view: route('/v1/tools/:toolId/view'),
    visibility: route('/v1/tools/:toolId/visibility'),
  },
  user: {
    nodeDefinitions: route('/v1/user/node-definitions'),
  },
  users: {
    root: route('/v1/users'),
    intercomToken: route('/v1/users/intercom-token'),
    marketingConsent: route('/v1/users/marketing-consent'),
    onboardingCredits: route('/v1/users/onboarding-credits'),
    preferences: route('/v1/users/preferences'),
    setActiveWorkspace: route('/v1/users/set-active-workspace'),
    signin: route('/v1/users/signin'),
    subscriptionTypeClaimRefresh: route('/v1/users/subscription-type-claim/refresh'),
    tour: route('/v1/users/tours/:tourId'),
  },
  workflows: {
    restore: route('/v1/workflows/restore'),
    comments: route('/v1/workflows/:workflowId/comments'),
    comment: route('/v1/workflows/:workflowId/comments/:commentId'),
    commentMentionableUsers: route('/v1/workflows/:workflowId/comments/mentionable-users'),
    figmaNodeConnect: route('/v1/workflows/:workflowId/figma-node/connect'),
    figmaNodeConnections: route('/v1/workflows/:workflowId/figma-node-connections'),
    figmaNodeCopy: route('/v1/workflows/:workflowId/figma-node/copy'),
    figmaNodeDisconnect: route('/v1/workflows/:workflowId/figma-node/disconnect'),
    figmaNodeInitialize: route('/v1/workflows/:workflowId/figma-node/initialize'),
    figmaNodeLastEdited: route('/v1/workflows/:workflowId/figma-node/last-edited'),
    figmaNodeLayers: route('/v1/workflows/:workflowId/figma-node/layers'),
    figmaNodeRefresh: route('/v1/workflows/:workflowId/figma-node/refresh'),
    figmaNodeVariablesBind: route('/v1/workflows/:workflowId/figma-node/variables/bind'),
    figmaNodeVariablesUpdate: route('/v1/workflows/:workflowId/figma-node/variables/update'),
    nodeMediaSign: route('/v1/workflows/:workflowId/nodes/:nodeId/media-sign'),
    nodeOutputVideoDownload: route('/v1/workflows/:workflowId/nodes/:nodeId/:outputId/download-video'),
  },
  workshops: {
    claim: route('/v1/workshops/claim'),
  },
  workspaces: {
    root: route('/v1/workspaces'),
    byId: route('/v1/workspaces/:workspaceId'),
    apiKeys: route('/v1/workspaces/:workspaceId/api-keys'),
    apiKey: route('/v1/workspaces/:workspaceId/api-keys/:apiKeyId'),
    apiKeysTermsAcceptance: route('/v1/workspaces/:workspaceId/api-keys-terms-acceptance'),
    byokProviderConfigs: route('/v1/workspaces/:workspaceId/byok-external-provider-configs'),
    byokProviderConfig: route('/v1/workspaces/:workspaceId/byok-external-provider-configs/:provider'),
    fonts: route('/v1/workspaces/:workspaceId/fonts'),
    font: route('/v1/workspaces/:workspaceId/fonts/:fontId'),
    invitationAccept: route('/v1/workspaces/members/invitation/:token/accept'),
    invitationValidate: route('/v1/workspaces/members/invitation/:token/validate'),
    members: route('/v1/workspaces/:workspaceId/members'),
    member: route('/v1/workspaces/:workspaceId/members/:memberId'),
    memberInvitationLink: route('/v1/workspaces/:workspaceId/members/:email/invitation-link'),
    memberInvitationResend: route('/v1/workspaces/:workspaceId/members/:email/invitation/resend'),
    memberInvitations: route('/v1/workspaces/:workspaceId/members/:email/invitations'),
    membersInvite: route('/v1/workspaces/:workspaceId/members/invite'),
    models: route('/v1/workspaces/:workspaceId/models'),
    modelApprovalRequests: route('/v1/workspaces/:workspaceId/models/approval-requests'),
    modelsApprove: route('/v1/workspaces/models/approve'),
    subscriptionCheckoutSession: route('/v1/workspaces/:workspaceId/subscriptions/checkout-session'),
    subscriptionCreditsCheckoutSession: route('/v1/workspaces/:workspaceId/subscriptions/credits/checkout-session'),
    subscriptionCustomerPortalSession: route('/v1/workspaces/:workspaceId/subscriptions/customer-portal-session'),
    subscriptionDowngrade: route('/v1/workspaces/:workspaceId/subscriptions/downgrade'),
    subscriptionPaymentMethod: route('/v1/workspaces/:workspaceId/subscriptions/payment-method'),
    subscriptionRestore: route('/v1/workspaces/:workspaceId/subscriptions/restore'),
    subscriptionSeatsPrice: route('/v1/workspaces/:workspaceId/subscriptions/seats/price'),
    subscriptionUpgrade: route('/v1/workspaces/:workspaceId/subscriptions/upgrade'),
  },
};
